<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { ChevronDoubleRightIcon } from '@heroicons/vue/24/solid'

import type { ContestColor } from './contestGame'

const emit = defineEmits(['on-swipe', 'update:resetSwipe'])
const props = withDefaults(
  defineProps<{
    text: string
    contestColor: ContestColor
    hasError: boolean
    disabled: boolean
  }>(),
  {
    disabled: false,
  },
)

const isSwiping = ref(false)
const swipeCircle = ref(null)
const swipeContainer = ref(null)
const swiped = ref(false)
const swipeDistance = ref(4)

const containerWidth = ref(0)
const circleWidth = ref(0)
let startX = 0

onMounted(() => {
  if (swipeContainer.value) {
    containerWidth.value = (swipeContainer.value as HTMLElement).getBoundingClientRect().width
  }
  if (swipeCircle.value) {
    circleWidth.value = (swipeCircle.value as HTMLElement).getBoundingClientRect().width
  }
})

const startSwipe = (event: MouseEvent | TouchEvent) => {
  if (props.disabled) return

  isSwiping.value = true
  startX = 'touches' in event ? event.touches[0].clientX : event.clientX
}

const onSwipe = (event: MouseEvent | TouchEvent) => {
  if (!isSwiping.value) return
  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
  const distance = clientX - startX

  swipeDistance.value = Math.min(Math.max(0, distance), containerWidth.value - circleWidth.value)
}

const endSwipe = () => {
  if (!isSwiping.value) return
  isSwiping.value = false

  if (swipeDistance.value >= (containerWidth.value - circleWidth.value) * 0.8) {
    swipeDistance.value = containerWidth.value - circleWidth.value
    swiped.value = true

    emit('on-swipe')
  } else {
    swipeDistance.value = 4
  }
}

watch(
  () => swipeDistance.value,
  () => {
    if (swipeDistance.value === 4) {
      swiped.value = false
    }
  },
)

watch(
  () => props.hasError,
  (newValue) => {
    if (newValue) swipeDistance.value = 4
  },
)
</script>

<template>
  <div class="relative mx-auto w-full lg:max-w-4xl">
    <div
      ref="swipeContainer"
      class="relative h-16 w-full overflow-hidden rounded-full bg-gray-100 duration-300"
      @mousedown="startSwipe"
      @mouseleave="endSwipe"
      @mousemove="onSwipe"
      @mouseup="endSwipe"
      @touchend="endSwipe"
      @touchmove="onSwipe"
      @touchstart="startSwipe"
    >
      <div
        class="pointer-events-none absolute left-0 top-0 h-full w-full overflow-hidden rounded-full"
        :style="{
          width: `${swipeDistance + 56}px`,
          background:
            swipeDistance !== 4
              ? `linear-gradient(to right, 
              ${contestColor.buttonColor}80 -${100 + swipeDistance}px,
              ${contestColor.buttonColor} ${swipeDistance + 56}px,
              #e2e8f0 ${swipeDistance + 56}px)`
              : 'transparent',
        }"
      />
      <div
        ref="swipeCircle"
        class="absolute left-0 top-1/2 z-10 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full shadow-lg"
        :style="{
          transform: `translate(${swipeDistance}px, -50%)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
          backgroundColor: contestColor.buttonColor,
          color: contestColor.buttonTextColor,
        }"
      >
        <ChevronDoubleRightIcon class="aspect-square w-5" />
      </div>
      <div
        class="relative z-0 flex h-full animate-pulse items-center justify-center text-gray-600 duration-300"
        :class="swiped && 'bg-primary'"
      >
        <span
          class="pl-5 text-base font-medium"
          :class="swipeDistance < 0.2 * containerWidth ? 'opacity-100' : 'opacity-0'"
        >
          {{ text }}
        </span>
      </div>
    </div>
  </div>
</template>
